import {
  Alert,
  Card,
  CardContent,
  Container,
  Grid,
  Link,
  Skeleton,
  Stack,
  Typography
} from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useCurrentOrg } from "~/providers/GraphqlProvider";
import FileUploadComponent from "./components/FileUploadComponent";
import FileUploadProgress from "./components/FileUploadProgress";
import ImportHistoryTable from "./components/ImportHistoryTable";
import ImportDetailsModal from "./components/ImportDetailsModal";
import ImportPreview, { NameAndTitle } from "./components/ImportPreviewer";
import { useSnackbar } from "~/hooks/useSnackbar";
import { DocumentNode, useLazyQuery, useMutation, useQuery } from "@apollo/client";

import { find } from "lodash";
import { DataImportStatusEnum, GetPatientDataImportQuery } from "~/graphql/__generated__/graphql";
import DownloadIcon from '@mui/icons-material/Download';
import { CustomDataImportSelectItem } from "./graphqlQueries";
import { retryFetch } from "@caredna-marlin/utils/client";

interface ImportScreenProps {
  resourceType: string,
  title: string,
  gql: {
    createImport: DocumentNode,
    previewImport: DocumentNode,
    executeImport: DocumentNode,
    deleteImport: DocumentNode,
    listImports: DocumentNode,
  },
  exampleColumnNamesAndTitles: NameAndTitle[],
  errorColumnNamesAndTitles: NameAndTitle[],
}

const ImportScreen: React.FC<ImportScreenProps> = ({resourceType, title, gql, exampleColumnNamesAndTitles, errorColumnNamesAndTitles}) => {
  const [polling, setPolling] = useState<boolean>(false);
  const [fileIsProcesing, setFileIsProcessing] = useState<boolean>(false);
  const [importProcessTriggered, setImportProcessTriggered] = useState<boolean>(false);
  const [fileUploadAttempted, setFileUploadAttempted] = useState<boolean>(false);
  const [shouldSkipPreviewData, setShouldSkipPreviewData] = useState<boolean>(true);
  const [fileToImport, setFileToImport] = useState<File | null>(null);
  const [selectedDataImport, setSelectedDataImport] = useState<CustomDataImportSelectItem | null>(null);

  const [createDataImport, { data: dataImportCreation, reset: resetDataImportCreate }] = useMutation(gql.createImport);
  const [executeImport] = useMutation(gql.executeImport);
  const [deleteDataImport] = useMutation(gql.deleteImport, { refetchQueries: 'active' });

  const topRef = useRef<HTMLDivElement | null>(null);

  const [fetchImportPreview, { data: importPreviewData, loading: loadingPreview, error }] = useLazyQuery(gql.previewImport);
  
  const previewDataColumnNames = importPreviewData?.dataImportPreview?.columnNames; 
  const errorColumnNames = importPreviewData?.dataImportPreview?.csvColumnNames;
  const previewData = importPreviewData?.dataImportPreview?.preview;

  const { setErrorMessage, messages: errorMessages } = useSnackbar();

  const [selectedImport, setSelectedImport] = useState<CustomDataImportSelectItem | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const handleRowClick = (row: CustomDataImportSelectItem) => {
    setSelectedImport(row);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedImport(null);
  };

  const onCreateDataImport = async (file: File) => {
    setSelectedDataImport(null);
    await createDataImport({
      variables: {
        resourceType,
        filename: file.name,
      },
    });
  };

  const {
    loading,
    data,
    startPolling,
    stopPolling,
  } = useQuery<GetPatientDataImportQuery>(gql.listImports); // Making result schema of these queries doesn't vary by resource type (e.g. patient and user are the same so okay to type based on patient)

  const cancelPolling = useCallback(() => {
    stopPolling();
    setPolling(false);
  }, [stopPolling, setPolling]);

  const triggerPolling = useCallback(() => {
    startPolling(3000);
    setPolling(true);
  }, [startPolling, setPolling]);

  const resetImport = useCallback(async () => {
    setImportProcessTriggered(false);
    setFileUploadAttempted(false);
    setSelectedDataImport(null);
    setFileToImport(null);
    setShouldSkipPreviewData(true);
    resetDataImportCreate();
    setErrorMessage([]);
    return cancelPolling();
  }, [
    cancelPolling,
    resetDataImportCreate,
    setErrorMessage,
    setSelectedDataImport,
    setFileUploadAttempted,
    setImportProcessTriggered,
    setFileToImport,
    setShouldSkipPreviewData,
  ]);

  useEffect(() => {
    if (!selectedDataImport) return;

    const currentStatus = (find(data?.dataImport, (d) => d.id === selectedDataImport.id))?.status;

    if (polling && currentStatus && selectedDataImport) {
      setSelectedDataImport(({ ...selectedDataImport, status: currentStatus }));
      if (shouldSkipPreviewData && currentStatus === DataImportStatusEnum.Uploaded) {
        setShouldSkipPreviewData(false);
        fetchImportPreview({
          variables: {
            dataImportId: selectedDataImport?.id
          }
        });
      } else if (currentStatus === DataImportStatusEnum.Successful) {
        resetImport();
      }
    }
  }, [
    importProcessTriggered,
    fetchImportPreview,
    shouldSkipPreviewData,
    polling,
    data?.dataImport,
    selectedDataImport,
    resetImport
  ]);

  // trigger polling if at least on import was still in progress.
  useEffect(() => {
    if (data?.dataImport?.length) {
      const dataImportInProgress = (find(data?.dataImport, (d) => d.status === DataImportStatusEnum.InProgress));
      if (dataImportInProgress?.id && !polling) {
        triggerPolling();
        setSelectedDataImport(dataImportInProgress);
        setImportProcessTriggered(true);
      }
    }
  }, [data?.dataImport, polling, triggerPolling]);

  useEffect(() => {
    if (error && errorMessages?.length === 0) {
      console.error("Error fetching data imports:", error);
      setErrorMessage('There was an unexpected error while previewing, please try again.');
    }
  }, [error, setErrorMessage, errorMessages]);

  const uploadFileToS3 = useCallback(async (url: string, file: File, dataImport: CustomDataImportSelectItem) => {
    try {
      setFileUploadAttempted(true);
      // Step 2: Upload the file to S3 using the pre-signed URL
      const result = await retryFetch(() => fetch(url, {
        method: "PUT",
        body: file,
        headers: {
          "Content-Type": "text/csv", // Set to text/csv for CSV files
          "x-amz-meta-filename": `${dataImport.filename}`,
        },
      }));

      if (result.ok) {
        console.log(`File ${file.name} uploaded successfully.`);

        setSelectedDataImport(dataImport);
        // Since file was uploaded begin polling for fetching data import record and start processing.
        triggerPolling();
        setErrorMessage([]); // clear previous error message if any
      } else {
        console.error(`Upload failed for ${file.name}: ${result.status}`);
        setErrorMessage('There was an unexpected error uploading the file, please try again.');
      }
      
    } catch (error) {
      console.error(`Error uploading file ${file.name}:`, error);
      setErrorMessage('There was an unexpected error uploading the file, please try again.');
    }

    setFileIsProcessing(false);
  }, [setErrorMessage, triggerPolling]);

  useEffect(() => {
    const currentDataImport = dataImportCreation?.createDataImport?.dataImport;
    const url = dataImportCreation?.createDataImport?.fileUploadUrl;

    if (!fileUploadAttempted && currentDataImport?.id && currentDataImport?.status !== DataImportStatusEnum.Successful && url && fileToImport) {
      (async () => {
        await uploadFileToS3(url, fileToImport, currentDataImport);
      })();
    }
  }, [
    dataImportCreation?.createDataImport,
    fileToImport,
    uploadFileToS3,
    fileUploadAttempted
  ]);

  const startImportProcess = useCallback(async () => {
    if (!selectedDataImport) {
      setErrorMessage('No DataImport selected!');
      return;
    }

    await executeImport({
      variables: {
        dataImportId: selectedDataImport.id,
      },
    });

    setImportProcessTriggered(true);
    triggerPolling();
  }, [executeImport, selectedDataImport, setErrorMessage, triggerPolling]);

  const onHandleFileUpload = async (file: File) => {
    await resetImport();
    setFileToImport(file);
    setFileIsProcessing(true);
    // Saves Data Import record with pending status
    await onCreateDataImport(file);
  };

  const onPreviewDataImport = useCallback(async (dataImport: CustomDataImportSelectItem) => {
    await resetImport();
    setSelectedDataImport(dataImport);
    await fetchImportPreview({variables: {
      dataImportId: dataImport.id
    }});
    topRef.current?.scrollIntoView();
  }, [fetchImportPreview, resetImport]);

  const onDeleteDataImport = async (dataImport: CustomDataImportSelectItem) => {
    await resetImport();
    if (!dataImport) {
      setErrorMessage('No DataImport selected!');
      return;
    }
    await deleteDataImport({
      variables: {
        dataImportId: dataImport?.id,
      },
      refetchQueries: [gql.listImports]
    });
  };

  const currentOrganization = useCurrentOrg();

  const shouldDisplayPreview = !!(!importProcessTriggered && selectedDataImport?.id);

  return (
    <Container maxWidth={false} sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={2} marginBottom={1}>
        <Grid item xs={12} ref={topRef}>
          <Stack direction='row'>
            <Typography variant="h6">
              {title} for {currentOrganization?.name}
            </Typography>
            {/* @NOTE: file extension is .txt but download property gaves it .csv at the end */}
            {/* Reason behind that is because either Cloudfront or S3 was preventing the content type to be CSV and had to force it */}
            <Link href={`/files/import_examples/${resourceType.toLowerCase()}.txt`} download={`${resourceType.toLowerCase()}_import_example.csv`}>
              <DownloadIcon style={{ verticalAlign: 'middle', marginRight: 10 }} />
              Download Import Template
            </Link>
          </Stack>
        </Grid>
        <Grid item xs={12}>
          <Card sx={{ width: 1, minWidth: "100%" }}>
            <CardContent>
              <FileUploadComponent onUploadFile={onHandleFileUpload} />
              {fileToImport && fileIsProcesing && <FileUploadProgress fileName={fileToImport.name} fileSize={fileToImport.size} />}
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12}>
          {shouldDisplayPreview && (
            <ImportPreview
              loading={selectedDataImport?.status === DataImportStatusEnum.Pending || loadingPreview}
              exampleColumnNames={previewDataColumnNames}
              errorColumnNames={errorColumnNames}
              exampleColumnNamesAndTitles={exampleColumnNamesAndTitles}
              errorColumnNamesAndTitles={errorColumnNamesAndTitles}
              errors={previewData?.errors}
              totalErrorCount={previewData?.totalErrorCount}
              validRecordCount={previewData?.validRecordCount}
              exampleRecords={previewData?.exampleRecords}
              onTriggerImportProcess={startImportProcess}
              onTriggerDeleteDataImport={() => {
                resetImport();
              }}
            />
          )}
        </Grid>

        {importProcessTriggered && (
          <Alert style={{ marginLeft: '15px' }} severity="warning">
            {selectedDataImport?.status === DataImportStatusEnum.InProgress ? 'Import in progress...' : 'Starting import...'}
          </Alert>
        )}
        
        <Grid item xs={12}>
          <Typography fontWeight={500} component="span">
            History
          </Typography>
          <Card sx={{ width: 1, minWidth: "100%" }}>
            <CardContent>
              <Stack
                spacing={2}
                justifyContent="space-between"
                minHeight={32}
              >
                {loading && (
                  <>
                    <Skeleton variant="text" sx={{ fontSize: "1.2rem" }} width={240} />
                    <Skeleton
                      variant="text"
                      sx={{ fontSize: "0.5em", mb: 0.5 }}
                    />
                    <Skeleton
                      variant="text"
                      sx={{ fontSize: "0.5em" }}
                      width={420}
                    />
                  </>
                )}

                {data?.dataImport && (
                  <ImportHistoryTable
                    onRowClick={handleRowClick}
                    onDeleteDataImport={(row) => onDeleteDataImport(row)}
                    onPreviewDataImport={(row) => onPreviewDataImport(row)}
                    data={data?.dataImport}
                  />
                )}

                <ImportDetailsModal 
                  open={modalOpen}
                  onClose={handleCloseModal}
                  selectedImport={selectedImport}
                />
              </Stack>
            </CardContent>
          </Card>

        </Grid>
      </Grid>
    </Container>
  );
}

export default ImportScreen;
